import { useState, useCallback, useMemo } from 'react';
import { syncSelectionWithConversations } from '../utils/bulkConversationActions';

/**
 * useInboxUI - UI Layer Hook
 *
 * Maneja el estado visual de selección de conversaciones en el inbox:
 * - isSelectingConversations: modo selección activo/inactivo
 * - selectedConversations: array de ids seleccionados
 * - Handlers para iniciar/finalizar selección, seleccionar/deseleccionar, limpiar selección
 * - syncConversationSelection: mantiene la selección alineada con la lista visible
 */
const useInboxUI = () => {
  // Estado visual de selección
  const [isSelectingConversations, setIsSelectingConversations] = useState(false);
  const [selectedConversations, setSelectedConversations] = useState([]);

  // Iniciar/finalizar modo selección
  const toggleSelectingConversations = useCallback(() => {
    setIsSelectingConversations((prev) => !prev);
    if (isSelectingConversations) {
      setSelectedConversations([]); // Limpiar selección al salir
    }
  }, [isSelectingConversations]);

  // Seleccionar/deseleccionar un id
  const toggleConversationSelection = useCallback((data) => {
    setSelectedConversations((prev) =>
      prev.findIndex((cid) => cid.id === data.id) !== -1
        ? prev.filter((cid) => cid.id !== data.id)
        : [...prev, data]
    );
  }, []);

  // Seleccionar todos
  const selectAllConversations = useCallback((ids) => {
    setSelectedConversations(ids);
  }, []);

  // Limpiar selección
  const clearConversationSelection = useCallback(() => {
    setSelectedConversations([]);
  }, []);

  // Salir de modo selección
  const exitSelectingConversations = useCallback(() => {
    setIsSelectingConversations(false);
    setSelectedConversations([]);
  }, []);

  // Quita de la selección las conversaciones que salieron de la lista (un filtro, un socket, una
  // asignación) para que no viajen ocultas en la siguiente acción masiva
  const syncConversationSelection = useCallback((conversations) => {
    setSelectedConversations((prev) => syncSelectionWithConversations(prev, conversations));
  }, []);

  // Verifica si un id está seleccionado
  const hasConversationSelected = useCallback(
    (id) => selectedConversations.findIndex((cid) => cid.id === id) !== -1,
    [selectedConversations]
  );

  return useMemo(() => ({
    isSelectingConversations,
    selectedConversations,
    toggleSelectingConversations,
    toggleConversationSelection,
    selectAllConversations,
    clearConversationSelection,
    exitSelectingConversations,
    syncConversationSelection,
    hasConversationSelected
  }), [
    isSelectingConversations,
    selectedConversations,
    toggleSelectingConversations,
    toggleConversationSelection,
    selectAllConversations,
    clearConversationSelection,
    exitSelectingConversations,
    syncConversationSelection,
    hasConversationSelected
  ]);
};

export default useInboxUI;
